import {
  StepFetcherResponse,
  FetcherFunction,
  FeedbackStepParams,
} from "../types/chat";
import {
  addFeedback,
  getFeedbackDefinitions,
} from "../pages/copilotMessage/services/copilot";
import { RcFile } from "antd/lib/upload/interface";
import {
  ACCEPTABLE_MEDIA_TYPES,
  MAX_UPLOAD_SIZE,
} from "../components/support/constants/Media";
import { AddingFeedbackInput } from "~/pages/copilotMessage/types/copilot";
import { FeedbackDefinitionName, FeedbackRateEnum } from "~/constants/feedback";
import { FEEDBACK_EVENTS } from "~/constants/events";

/**
 * Feedback Definitions Fetcher Function
 */
export const fetchFeedbackDefinitions: FetcherFunction<
  FeedbackStepParams
> = async (params?: FeedbackStepParams): Promise<StepFetcherResponse> => {
  try {
    const { conversationId, conversationResponseId } = params || {};
    let onSubmitText = "";
    let feedbackSteps;
    const otherResponseText =
      "To help us better support you, could you share why no action was taken?";

    const definitions = await getFeedbackDefinitions();

    switch (params?.feedbackRate) {
      case FeedbackRateEnum.LIKE:
        onSubmitText =
          "Thank you for your positive feedback. We're glad our response was helpful!";

        feedbackSteps = definitions
          .filter((def) => def.rate === FeedbackRateEnum.LIKE)
          .map((def) => {
            if (def.name === FeedbackDefinitionName.LIKE_BUT_NO_ACTION) {
              return {
                stepId: def.id,
                title: def.description,
                isRoot: false,
                inputConfig: {
                  enabled: true,
                  attachments: false,
                  submitHandler: async (text: string) => {
                    return await submitFeedback({
                      conversationId,
                      conversationResponseId,
                      definitionId: def.id,
                      description: text,
                    });
                  },
                  successMessage: onSubmitText,
                },
                renderObjects: [
                  {
                    responseText: otherResponseText,
                  },
                ],
                onCancel: async () => {
                  await submitFeedback({
                    conversationId,
                    conversationResponseId,
                    definitionId: def.id,
                    description: def.description,
                  });
                },
              };
            }
            return {
              stepId: def.id,
              title: def.description,
              isRoot: false,
              directSubmitHandler: async () => {
                return await submitFeedback({
                  conversationId,
                  conversationResponseId,
                  definitionId: def.id,
                  description: def.description,
                });
              },
              renderObjects: [
                {
                  responseText: onSubmitText,
                },
              ],
            };
          });
        break;
      case FeedbackRateEnum.DISLIKE:
        onSubmitText =
          "Thank you for your feedback. We will use this to improve our responses.";

        feedbackSteps = definitions
          .filter((def) => def.rate === FeedbackRateEnum.DISLIKE)
          .map((def) => {
            // For "Other" feedback (ID: 5), show input field
            if (def.name === FeedbackDefinitionName.OTHER) {
              return {
                stepId: def.id,
                title: def.description,
                isRoot: false,
                inputConfig: {
                  enabled: true,
                  attachments: false,
                  submitHandler: async (text: string) => {
                    return await submitFeedback({
                      conversationId,
                      conversationResponseId,
                      definitionId: def.id,
                      description: text,
                    });
                  },
                  successMessage: onSubmitText,
                },
                renderObjects: [
                  {
                    responseText: otherResponseText,
                  },
                ],
                onCancel: async () => {
                  await submitFeedback({
                    conversationId,
                    conversationResponseId,
                    definitionId: def.id,
                    description: def.description,
                  });
                },
              };
            }

            // For other feedback types, create direct submission steps
            return {
              stepId: def.id,
              title: def.description,
              isRoot: false,
              // No inputConfig for direct submission
              renderObjects: [
                {
                  responseText: onSubmitText,
                },
              ],
              // Add custom submit handler for direct feedback
              directSubmitHandler: async () => {
                return await submitFeedback({
                  conversationId,
                  conversationResponseId,
                  definitionId: def.id,
                  description: def.description, // Use the definition description as feedback
                });
              },
            };
          });

        break;
      default:
        break;
    }

    return { steps: feedbackSteps };
  } catch (error) {
    return {
      responseText:
        "I'm sorry, I couldn't retrieve the feedback options at the moment. The feedback service might be unavailable. Please try again later.",
      steps: [],
    };
  }
};

/**
 * Submit support request function
 */
export const submitSupportRequest = async (
  text: string,
  attachments?: RcFile[],
): Promise<boolean> => {
  // Check file size
  if (attachments && attachments.length > 0) {
    const sumOfFilesSize = attachments
      .map((x) => x.size)
      .reduce((acc, currentValue) => acc + currentValue, 0);

    if (sumOfFilesSize > MAX_UPLOAD_SIZE) {
      throw new Error("Total file size exceeds the maximum allowed size.");
    }

    // Validate file types
    const invalidFiles = attachments.filter(
      (file) =>
        !ACCEPTABLE_MEDIA_TYPES.some(
          (mediaType) => mediaType.type === file.type,
        ),
    );

    if (invalidFiles.length > 0) {
      throw new Error(
        `Some files have unsupported formats. Acceptable formats: ${ACCEPTABLE_MEDIA_TYPES.map(
          (x) => x.extension,
        ).join(", ")}`,
      );
    }
  }

  try {
    // Import required modules
    const { CreateSupport } = await import(
      "../components/support/services/createSupport"
    );
    const { getWindowEnv } = await import("../helpers/envConfig");
    const { parseJwt } = await import("../helpers/jwt");

    // Get authentication token
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) {
      throw new Error("You need to be logged in to submit a support request.");
    }

    // Parse token for user info
    const parsedToken = parseJwt(token);
    const supportInfo = {
      name: parsedToken.fullName,
      companyName: parsedToken.CompanyName,
      email: parsedToken.email,
      groupSid: parsedToken.groupsid,
    };

    // Create FormData for submission
    const formData = new FormData();
    formData.append("Description", text);

    // Add attachment files
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append("Files", file);
      });
    }

    // Add user and company info
    formData.append("Email", supportInfo.email);
    formData.append("UserDisplayName", supportInfo.name);
    formData.append("CompanyName", supportInfo.companyName);
    formData.append("CompanyId", supportInfo.groupSid);

    // Add environment info
    const { DIGITAL_OPERATION_URL, XSIGHT_VERSION } = getWindowEnv();
    formData.append(
      "Environment",
      DIGITAL_OPERATION_URL || window.location.origin,
    );
    formData.append("WebVersion", XSIGHT_VERSION || "1.0.0");

    // Submit the support request
    const result = await CreateSupport(formData, supportInfo.groupSid);

    return !!result;
  } catch (error) {
    throw new Error("Error submitting support request");
  }
};

interface SubmitFeedbackParams {
  conversationId?: string;
  conversationResponseId?: string;
  definitionId: number | string;
  description: string;
}

export const submitFeedback = async (
  params: SubmitFeedbackParams,
): Promise<boolean> => {
  const {
    conversationId = "unknown",
    conversationResponseId = "unknown",
    definitionId,
    description,
  } = params;

  if (!conversationId || !conversationResponseId || !definitionId) {
    throw new Error("Missing required parameters for feedback submission");
  }

  const feedbackInput: AddingFeedbackInput = {
    CopilotConversationId: conversationId,
    CopilotResponseId: conversationResponseId,
    DefinitionId: Number(definitionId),
    Description: description,
  };

  try {
    await addFeedback(feedbackInput);

    // Dispatch feedback submitted event

    if (feedbackInput.DefinitionId != undefined) {
      const event = new CustomEvent(FEEDBACK_EVENTS.FEEDBACK_SUBMITTED);
      window.dispatchEvent(event);
    }

    return true;
  } catch (error) {
    throw new Error("Error submitting feedback");
  }
};
